import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { qs, apiError } from "@/lib/api";
import { fmtCr, fmtDateShort, downloadRows } from "@/lib/stock";
import { TH } from "@/components/StockBits";
import { Button } from "@/components/ui/button";
import { ChevronDown, ChevronRight, Download } from "lucide-react";

const dosTone = (d) => (d == null ? "text-muted-foreground" : d > 90 ? "text-red-700 font-bold" : d > 60 ? "text-amber-700 font-semibold" : d < 15 ? "text-emerald-700 font-semibold" : "");
const ssiTone = (s) => (s == null ? "text-muted-foreground" : s >= 2 ? "text-red-700 font-bold" : s >= 1.3 ? "text-amber-700 font-semibold" : "");
const pctTone = (p) => (p >= 25 ? "text-red-700 font-bold" : p >= 10 ? "text-amber-700 font-semibold" : "");

function Cells({ r, buckets, mode }) {
  return buckets.map((b) => {
    const c = r.cells?.[b.label];
    const v = mode === "value" ? c?.value : c?.units;
    return <td key={b.label} className="px-2 py-1 text-right num" title={mode === "value" ? `${c?.units ?? 0} units` : fmtCr(c?.value)}>{v ? (mode === "value" ? fmtCr(v) : v) : <span className="text-muted-foreground/40">·</span>}</td>;
  });
}

function Velocity({ r, has }) {
  if (!has) return null;
  const dos = r.dos == null ? (r.units && r.retail_3m === 0 ? "∞" : "—") : `${r.dos} d`;
  return (
    <>
      <td className="px-2 py-1 text-right num">{r.retail_3m ?? "—"}</td>
      <td className="px-2 py-1 text-right num text-muted-foreground">{r.per_month ?? "—"}</td>
      <td className={`px-2 py-1 text-right num ${dosTone(r.dos)}`} title="Days of supply = units in stock ÷ retail per day (last 3 months)">{dos}</td>
      <td className={`px-2 py-1 text-right num ${ssiTone(r.ssi)}`} title="Slow-spec index = share of aged stock ÷ share of retail; >1 means this spec ages faster than it sells">{r.ssi == null ? "—" : r.ssi >= 9.99 ? "no sales" : r.ssi}</td>
    </>
  );
}

function Common({ r, mode }) {
  return (
    <>
      <td className="px-2 py-1 text-right num font-semibold">{r.units}</td>
      <td className="px-2 py-1 text-right num text-muted-foreground">{fmtCr(r.value)}</td>
      <td className={`px-2 py-1 text-right num ${r.aged_units ? "text-red-700 font-semibold" : "text-muted-foreground"}`}>{mode === "value" ? fmtCr(r.aged_value) : r.aged_units}</td>
      <td className={`px-2 py-1 text-right num ${pctTone(r.pct_aged)}`}>{r.pct_aged}%</td>
      <td className="px-2 py-1 text-right num text-muted-foreground">{r.oldest ?? "—"}{r.oldest != null && " d"}</td>
    </>
  );
}

export function AgeingSummary({ query, locatorBase = {}, testId = "stock-summary", refreshKey = 0 }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  const [open, setOpen] = useState({});
  const [mode, setMode] = useState("units");
  const key = JSON.stringify(query);
  useEffect(() => { api.get(`/stock/summary?${qs(JSON.parse(key))}`).then(({ data }) => { setD(data); setErr(null); }).catch((e) => setErr(apiError(e))); }, [key, refreshKey]);
  if (err) return <div className="text-xs text-red-600" data-testid={`${testId}-error`}>{err}</div>;
  if (!d) return null;
  const buckets = d.buckets || [];
  const has = d.has_retail;
  const allOpen = d.rows.length > 0 && d.rows.every((r) => open[r.model]);
  const toggleAll = () => setOpen(allOpen ? {} : Object.fromEntries(d.rows.map((r) => [r.model, true])));
  const exportCsv = () => {
    const head = ["Model", "Variant", "Units", "Value", ...buckets.map((b) => b.label), "Aged units", "Aged value", "% aged", "Oldest", "Retail 3m", "Per month", "Days of supply", "Slow-spec index"];
    const line = (m, r, name) => [m, name, r.units, r.value, ...buckets.map((b) => r.cells?.[b.label]?.units ?? 0), r.aged_units, r.aged_value, r.pct_aged, r.oldest, r.retail_3m, r.per_month, r.dos, r.ssi];
    downloadRows("stock_ageing_summary.csv", head, d.rows.flatMap((r) => [line(r.model, r, "ALL"), ...r.variants.map((v) => line(r.model, v, v.name))]));
  };
  const cols = 6 + buckets.length + (has ? 4 : 0);
  return (
    <div className="border bg-card rounded-md overflow-auto" data-testid={testId}>
      <div className="px-4 py-3 border-b flex flex-wrap items-center gap-3 sticky top-0 bg-card z-10">
        <h3 className="font-heading text-sm font-semibold">Ageing summary by model → variant <span className="text-muted-foreground font-normal text-xs">{d.scope?.label} · click a model to open its variants · a variant opens the locator</span></h3>
        <span className="text-[11px] text-muted-foreground" data-testid={`${testId}-window`}>{has ? `retail velocity ${fmtDateShort(d.retail_window.from)} – ${fmtDateShort(d.retail_window.to)}` : "upload the retail feed to see velocity, days of supply and slow-spec index"}</span>
        <div className="ml-auto flex items-center gap-1 text-[11px]">
          {[["units", "Units"], ["value", "₹"]].map(([k, l]) => <button key={k} onClick={() => setMode(k)} className={`rounded border px-2 py-0.5 ${mode === k ? "bg-foreground text-background" : "text-muted-foreground hover:bg-secondary"}`} data-testid={`${testId}-mode-${k}`}>{l}</button>)}
          <button onClick={toggleAll} className="rounded border px-2 py-0.5 text-muted-foreground hover:bg-secondary" data-testid={`${testId}-toggle-all`}>{allOpen ? "Collapse all" : "Expand all"}</button>
          <Button size="sm" variant="ghost" className="h-6 px-2" onClick={exportCsv} data-testid={`${testId}-export`}><Download size={12} /></Button>
        </div>
      </div>
      <table className="w-full text-xs">
        <thead className="border-b">
          <tr>
            <th className={TH}>Model / variant</th><th className={`${TH} text-right`}>Units</th><th className={`${TH} text-right`}>Value</th>
            {buckets.map((b) => <th key={b.label} className={`${TH} text-right`}><span className="inline-block h-2 w-2 rounded-sm mr-1" style={{ background: b.color }} />{b.label}</th>)}
            <th className={`${TH} text-right`}>Aged</th><th className={`${TH} text-right`}>% aged</th><th className={`${TH} text-right`}>Oldest</th>
            {has && <><th className={`${TH} text-right`}>Retail 3 m</th><th className={`${TH} text-right`}>/ month</th><th className={`${TH} text-right`}>Days of supply</th><th className={`${TH} text-right`}>Slow-spec</th></>}
          </tr>
        </thead>
        <tbody>
          {d.rows.map((r) => (
            <RowGroup key={r.model} r={r} buckets={buckets} mode={mode} has={has} open={!!open[r.model]} toggle={() => setOpen({ ...open, [r.model]: !open[r.model] })} locatorBase={locatorBase} testId={testId} />
          ))}
          {d.rows.length === 0 && <tr><td colSpan={cols} className="px-3 py-6 text-center text-muted-foreground">No stock in this view</td></tr>}
        </tbody>
        {d.rows.length > 0 && (
          <tfoot className="border-t bg-secondary/40 font-semibold">
            <tr data-testid={`${testId}-total`}><td className="px-2 py-1.5">Total · {d.rows.length} models</td><Common r={d.total} mode={mode} /><Cells r={d.total} buckets={buckets} mode={mode} />{has && <td colSpan={4} />}</tr>
          </tfoot>
        )}
      </table>
    </div>
  );
}

function RowGroup({ r, buckets, mode, has, open, toggle, locatorBase, testId }) {
  return (
    <>
      <tr className="border-b hover:bg-muted/50 cursor-pointer" onClick={toggle} data-testid={`${testId}-model-${r.model}`}>
        <td className="px-2 py-1.5 whitespace-nowrap font-semibold"><span className="inline-flex items-center gap-1">{open ? <ChevronDown size={13} /> : <ChevronRight size={13} />}{r.model}<span className="text-[10px] text-muted-foreground font-normal ml-1">{r.variants.length} variants</span></span></td>
        <Common r={r} mode={mode} /><Cells r={r} buckets={buckets} mode={mode} /><Velocity r={r} has={has} />
      </tr>
      {open && r.variants.map((v) => (
        <tr key={v.variant_id} className="border-b last:border-0 bg-secondary/20 hover:bg-muted/60" data-testid={`${testId}-variant-${v.variant_id}`}>
          <td className="px-2 py-1 pl-7 whitespace-nowrap"><Link to={`/stock/locator?${qs({ variant_id: v.variant_id, ...locatorBase })}`} className="hover:underline" data-testid={`${testId}-variant-link-${v.variant_id}`}>{v.name}</Link>{v.fuel && <span className="ml-1.5 text-[9px] rounded bg-secondary px-1 text-muted-foreground">{v.fuel}</span>}</td>
          <Common r={v} mode={mode} /><Cells r={v} buckets={buckets} mode={mode} /><Velocity r={v} has={has} />
        </tr>
      ))}
    </>
  );
}
